import { useCallback, useMemo } from 'react'

import type { QueryObserverResult } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { t } from 'i18next'
import { useFormContext } from 'react-hook-form'

import { CompradorPJUseCase } from '@/features/proposta/entities/Comprador/use-case/comprador-pj-use-case'
import type { TCompradoresCadastrados } from '@/features/proposta/interfaces/proposta.type'
import type { CompradorPessoaJuridicaSchemaInput } from '@/features/proposta/schemas/compradores/comprador-pj.schema'
import type { TConta } from '@/shared/interfaces/conta.types'
import { showToast } from '@/shared/lib/sonner'

export function useDadosCompradorPJ({
  onNext,
  compradores,
  nacionalidadePrincipalId,
  buscarPorCNPJ,
  emailsJaVinculados,
}: {
  nacionalidadePrincipalId?: string
  onNext: () => void
  buscarPorCNPJ: () => Promise<QueryObserverResult<TConta, AxiosError>>
  compradores: TCompradoresCadastrados
  emailsJaVinculados: Record<string, string>
}) {
  const { trigger, setValue, reset, watch } = useFormContext<CompradorPessoaJuridicaSchemaInput>()
  const { buscouCnpj, cnpj } = watch('dados')
  const cnpjRaw = cnpj?.replaceAll(/\D/g, '')
  const useCase = useMemo(
    () => new CompradorPJUseCase(compradores, emailsJaVinculados, nacionalidadePrincipalId ?? ''),
    [compradores, emailsJaVinculados, nacionalidadePrincipalId],
  )

  const validateOnNext = useCallback(async () => {
    const { ok, error } = useCase.validarAvanco(cnpjRaw, !!buscouCnpj)
    if (!ok) {
      if (error === 'MISSING_CNPJ') return trigger('dados.cnpj')
      if (error === 'NEED_SEARCH')
        return showToast({
          variant: 'error',
          message: t('proposta:cadastro_compradores.steps.dados_comprador_pj.busca.obrigatorio'),
        })
    }
    if (await trigger('dados')) onNext()
  }, [useCase, cnpjRaw, buscouCnpj, trigger, onNext])

  const handleBuscarConta = useCallback(async () => {
    if (useCase.devePularBuscaPoisHaDuplicidade(cnpjRaw)) {
      showToast({
        variant: 'error',
        message: t('proposta:cadastro_compradores.steps.dados_comprador_pj.busca.erro_duplicidade'),
      })
      return
    }
    const result = await buscarPorCNPJ()
    const fetchResult = useCase.interpretarResultadoBuscaManual(result)

    switch (fetchResult.type) {
      case 'SUCCESS':
        showToast({
          variant: 'success',
          message: t('proposta:cadastro_compradores.steps.dados_comprador_pj.busca.sucesso'),
        })
        reset(fetchResult.patch)
        break
      case 'NOT_FOUND':
        showToast({
          variant: 'info',
          message: t('proposta:cadastro_compradores.steps.dados_comprador_pj.busca.nao_encontrado'),
        })
        setValue('dados.buscouCnpj', true)
        break
      case 'ERROR':
        showToast({ variant: 'error', message: t('common:errors_gerais.padrao') })
        break
    }
  }, [buscarPorCNPJ, cnpjRaw, reset, setValue, useCase])

  const handleLimpar = useCallback(() => {
    reset(useCase.limparFormulario())
  }, [reset, useCase])

  return {
    handlers: {
      validateOnNext,
      handleBuscarConta,
      handleLimpar,
    },
  }
}
